import { isAutoScrollEnabled, scrollToBottom } from '../../utils/scroll';
import { ChatOrchestratorBase, type Constructor } from './orchestrator-base';

interface CallRecord {
  status: 'calling' | 'done' | 'error';
  inputs?: Record<string, unknown>;
  error?: string;
}

/**
 * Renders/updates/removes the tool-call indicator bubble in the shadow DOM.
 * Pure DOM manipulation — self-contained aside from the shared `shadow` field.
 *
 * Repeated calls to the same tool within one message are collapsed into a single
 * card: a bare spinner (no details) while any call is still running, then swapped
 * for a card showing only the last call's own params/status once every call for
 * that tool has settled.
 */
export function withToolIndicatorDom<TBase extends Constructor<ChatOrchestratorBase>>(Base: TBase) {
  return class extends Base {
    private toolCallGroups = new Map<string, Map<string, CallRecord>>();
    private toolCallLocation = new Map<string, { messageId: string; toolName: string }>();

    protected appendToolIndicator(messageId: string, toolCallId: string, toolName: string, inputs?: Record<string, unknown>): void {
      const key = `${messageId}::${toolName}`;
      let group = this.toolCallGroups.get(key);
      if (!group) {
        group = new Map();
        this.toolCallGroups.set(key, group);
      }
      group.set(toolCallId, { status: 'calling', inputs });
      this.toolCallLocation.set(toolCallId, { messageId, toolName });

      this.refreshCard(messageId, toolName);

      const scrollContainer = this.shadow.querySelector('.panel-body') as HTMLDivElement | null;
      if (isAutoScrollEnabled(scrollContainer)) {
        scrollToBottom(scrollContainer);
      }
    }

    protected updateToolIndicator(toolCallId: string, status: 'done' | 'error', error?: string): void {
      const loc = this.toolCallLocation.get(toolCallId);
      if (!loc) return;
      const group = this.toolCallGroups.get(`${loc.messageId}::${loc.toolName}`);
      const record = group?.get(toolCallId);
      if (!record) return;

      record.status = status;
      record.error = error;

      this.refreshCard(loc.messageId, loc.toolName);
    }

    protected removeToolIndicators(toolCallIds: string[]): void {
      for (const toolCallId of toolCallIds) {
        const loc = this.toolCallLocation.get(toolCallId);
        if (!loc) continue;
        this.toolCallLocation.delete(toolCallId);

        const key = `${loc.messageId}::${loc.toolName}`;
        const group = this.toolCallGroups.get(key);
        group?.delete(toolCallId);
        if (group && group.size === 0) this.toolCallGroups.delete(key);

        this.refreshCard(loc.messageId, loc.toolName);
      }
    }

    /** Single source of truth for what's on screen for one (message, tool name) pair -
     *  nothing, a bare pending spinner, or the last call's settled card. */
    private refreshCard(messageId: string, toolName: string): void {
      const group = this.toolCallGroups.get(`${messageId}::${toolName}`);
      if (!group || group.size === 0) {
        this.removeRenderedCard(messageId, toolName);
        return;
      }

      const stillPending = Array.from(group.values()).some((r) => r.status === 'calling');
      if (stillPending) {
        this.renderPendingCard(messageId, toolName);
        return;
      }

      const lastId = Array.from(group.keys()).pop()!;
      this.renderSettledCard(messageId, toolName, lastId, group.get(lastId)!);
    }

    private findMessageContent(messageId: string): Element | null {
      return this.shadow.querySelector(`[data-message-id="${messageId}"] .message-content`);
    }

    private removeRenderedCard(messageId: string, toolName: string): void {
      const msgEl = this.findMessageContent(messageId);
      if (!msgEl) return;
      Array.from(msgEl.querySelectorAll<HTMLElement>('.tool-call'))
        .find((el) => el.getAttribute('data-tool-name') === toolName)
        ?.remove();
    }

    private renderPendingCard(messageId: string, toolName: string): void {
      const msgEl = this.findMessageContent(messageId);
      if (!msgEl) return;
      this.removeRenderedCard(messageId, toolName);

      const indicator = document.createElement('div');
      indicator.className = 'tool-call';
      indicator.setAttribute('data-tool-name', toolName);

      const header = document.createElement('div');
      header.className = 'tool-call-header';

      const spinner = document.createElement('span');
      spinner.className = 'tool-call-spinner';
      header.appendChild(spinner);

      const nameEl = document.createElement('span');
      nameEl.className = 'tool-call-name';
      nameEl.textContent = toolName;
      header.appendChild(nameEl);

      indicator.appendChild(header);
      msgEl.appendChild(indicator);
    }

    private renderSettledCard(messageId: string, toolName: string, toolCallId: string, record: CallRecord): void {
      const msgEl = this.findMessageContent(messageId);
      if (!msgEl) return;
      this.removeRenderedCard(messageId, toolName);

      const indicator = document.createElement('div');
      indicator.className = `tool-call tool-call-${record.status}`;
      indicator.setAttribute('data-tool-call-id', toolCallId);
      indicator.setAttribute('data-tool-name', toolName);

      const header = document.createElement('div');
      header.className = 'tool-call-header';

      const icon = document.createElement('span');
      icon.className = record.status === 'done' ? 'tool-call-check' : 'tool-call-error-icon';
      icon.textContent = record.status === 'done' ? '✓' : '✗';
      header.appendChild(icon);

      const nameEl = document.createElement('span');
      nameEl.className = 'tool-call-name';
      nameEl.textContent = toolName;
      header.appendChild(nameEl);

      indicator.appendChild(header);

      const hasInputs = !!record.inputs && Object.keys(record.inputs).length > 0;
      if (hasInputs) {
        const toggleBtn = document.createElement('button');
        toggleBtn.className = 'tool-call-inputs-toggle';
        toggleBtn.textContent = '▶ inputs';
        header.appendChild(toggleBtn);

        const inputsPanel = document.createElement('div');
        inputsPanel.className = 'tool-call-inputs tool-call-inputs-hidden';

        for (const [k, v] of Object.entries(record.inputs!)) {
          const row = document.createElement('div');
          row.className = 'tool-call-input-row';

          const keyEl = document.createElement('span');
          keyEl.className = 'tool-call-input-key';
          keyEl.textContent = `${k}:`;
          row.appendChild(keyEl);

          const valEl = document.createElement('span');
          valEl.className = 'tool-call-input-val';
          valEl.textContent = JSON.stringify(v);
          row.appendChild(valEl);

          inputsPanel.appendChild(row);
        }

        toggleBtn.addEventListener('click', () => {
          const isHidden = inputsPanel.classList.toggle('tool-call-inputs-hidden');
          toggleBtn.textContent = isHidden ? '▶ inputs' : '▼ inputs';
        });

        indicator.appendChild(inputsPanel);
      }

      if (record.status === 'error' && record.error) {
        const detailsEl = document.createElement('div');
        detailsEl.className = 'tool-call-details';
        detailsEl.textContent = record.error;
        indicator.appendChild(detailsEl);
      }

      msgEl.appendChild(indicator);

      const scrollContainer = this.shadow.querySelector('.panel-body') as HTMLDivElement | null;
      if (isAutoScrollEnabled(scrollContainer)) {
        scrollToBottom(scrollContainer);
      }
    }
  };
}
